<template>
  <div>
    <div>
      <p>
        <span>
          Please enter the login code to validate your new Multi Factor Authentication (MFA) contact information. Login code will be valid for limited time. If you entered office email and cell phone # 
          you will have received separate login code(s) and you will have to validate both. After validating both click 'Next' to navigate to your RI DMV Online Customer profile. 
        </span> <br />
        <span>
          Your contact information will be saved against your RI record and used for future electronic Multi Factor Authentication (MFA).
        </span>
      </p>
      <p class="err-email" v-if="contactMfaRequestEmail.length > 0 && !contactMfaRequestEmail[0].isSucess">
        Unable to send login code to provided office email
      </p>

      <!-- Office Email Validation -->
      <div class="user-con-validate" v-if="updateContact.email || updateContact.officeEmail">
        <div class="user-con-details">
          <div class="user-details">
            <span class="user-email-heading">Office Email</span> 
            <div class="verify-updateContact">
              <label for="officeEmail">{{ updateContactInfo.email || updateContactInfo.officeEmail }}</label>
              <img v-if="contactVerifyOfficeEmail" class="verify-icon" src="static/verified-icon.png" alt="Office email is verified">
            </div>
          </div>
          <div class="user-details" v-if="!contactVerifyOfficeEmail">
            <div>
              <label for="loginCodeOfficeEmail" class="login-code-text">Login code</label>
              <input id="loginCodeOfficeEmail" type="text" v-model="otpOfficeEmail" maxlength="12">
              <p v-if="errOfficeEmail" class="err-msg">{{ errOfficeEmail }}</p>
            </div>
          </div>
        </div>
        <div class="user-response" v-if="!contactVerifyOfficeEmail">
          <div class="btn-otp">
            <CustomButton @click="validateOtp('OfficeEmail')">Validate login code</CustomButton>
            <CustomButton @click="resendOtp('OfficeEmail')" class="resend-code">Resend login code</CustomButton>
          </div>
        </div>
      </div>

      <p v-if="contactMfaRequestPhone.length > 0 && !contactMfaRequestPhone[0].isSucess" class="err-phone">
        Unable to send login code to provided cell phone
      </p>

      <!-- Cell Phone Validation -->
      <div class="user-con-validate" v-if="updateContact.phone || updateContact.cellPhone">
        <div class="user-con-details">
          <div class="user-details">
            <div class="user-phone-details">
              <span class="user-phone-heading">Cell Phone</span>
              <span class="user-phone-text">(Message and data rates may apply)</span>
            </div>
            <div class="verify-updateContact">
              <label for="cellPhone">{{ updateContactInfo.phone || updateContactInfo.cellPhone }}</label>
              <img v-if="contactVerifyCellPhone" class="verify-icon" src="static/verified-icon.png" alt="Cell phone is verified">
            </div>
          </div>
          <div class="user-details" v-if="!contactVerifyCellPhone">
            <div>
              <label for="loginCodeCellPhone" class="login-code-text">Login code</label>
              <input id="loginCodeCellPhone" type="text" v-model="otpCellPhone" maxlength="12">
              <p v-if="errCellPhone" class="err-msg">{{ errCellPhone }}</p>
            </div>
          </div>
        </div>
        <div class="user-response" v-if="!contactVerifyCellPhone">
          <div class="btn-otp">
            <CustomButton @click="validateOtp('CellPhone')">Validate login code</CustomButton>
            <CustomButton @click="resendOtp('CellPhone')" class="resend-code">Resend login code</CustomButton>
          </div>
        </div>
      </div>

      <!-- Non-MFA Fields Info (if updated) -->
      <div v-if="hasNonMfaFields" class="non-mfa-info">
        <p class="info-message">
          <strong>Note:</strong> The following information will be updated without MFA validation:
        </p>
        <ul>
          <li v-if="updateContact.contactPersonName">Contact Name</li>
          <li v-if="updateContact.officePhone">Office Phone</li>
          <li v-if="updateContact.officeFax">Office Fax</li>
        </ul>
      </div>
    </div>

    <BaseButtonGroup>
      <button type="button" @click="handleRouting('previous')">Previous</button>
      <button type="submit" class="app-btn-primary" @click.prevent="handleRouting('next')" :disabled="!isNextButtonEnabled">
        Next
      </button>
    </BaseButtonGroup>

  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import * as COMMONS from '../../../store/_commons'; 
import BaseButtonGroup from '@/components/ui/BaseButtonGroup.vue';
import CustomButton from '../../CustomButton.vue';
import { usePhoneFormatter } from '@/composables/usePhoneFormatter';

// Vuex Store
const store = useStore();

// Vue Router
const router = useRouter();

// Reactive state
const otpCellPhone = ref('');
const otpOfficeEmail = ref('');
const errOfficeEmail = ref('');
const errCellPhone = ref('');
const emailCount = ref(0);
const phoneCount = ref(0);

// Phone composable
const { formatPhoneForRims } = usePhoneFormatter();

// Computed Properties
const updateContact = computed(() => store.state.guestLogin.updateContact);
const updateContactInfo = computed(() => store.state.guestLogin.contactUpdateInfo);
const contactVerifyOfficeEmail = computed(() => store.state.guestLogin.contactVerifyEmail);
const contactVerifyCellPhone = computed(() => store.state.guestLogin.contactVerifyPhone);

const contactMfaRequestPhone = computed(() => {
  const responses = store.state.guestLogin.contactMfaRequest.responses;
  return responses ? responses.filter(item => item.mfaType === 1) : [];
});

const contactMfaRequestEmail = computed(() => {
  const responses = store.state.guestLogin.contactMfaRequest.responses;
  return responses ? responses.filter(item => item.mfaType === 2) : [];
});

// Check if non-MFA fields were updated
const hasNonMfaFields = computed(() => {
  return updateContact.value.contactPersonName ||
         updateContact.value.officePhone || 
         updateContact.value.officeFax;
});

const isNextButtonEnabled = computed(() => {
  const hasOfficeEmail = updateContactInfo.value.officeEmail|| updateContactInfo.value?.email;
  const hasCellPhone = updateContactInfo.value.cellPhone|| updateContactInfo.value?.phone;
  const isOfficeEmailVerified = contactVerifyOfficeEmail.value;
  const isCellPhoneVerified = contactVerifyCellPhone.value;
  
  let isEnabled = false;

  // If only non-MFA fields are being updated, enable next button
  if (!hasOfficeEmail && !hasCellPhone && hasNonMfaFields.value) {
    return true;
  }

  // If only office email is being updated
  if (hasOfficeEmail && !hasCellPhone) {
    isEnabled = isOfficeEmailVerified;
  }

  // If only cell phone is being updated
  if (!hasOfficeEmail && hasCellPhone) {
    isEnabled = isCellPhoneVerified;
  }

  // If both office email and cell phone are being updated
  if (hasOfficeEmail && hasCellPhone) {
    isEnabled = isOfficeEmailVerified && isCellPhoneVerified;
  }

  return isEnabled; 
});


// Lifecycle Hooks
onMounted(() => {
  window.scroll(0, 0);
  document.body.setAttribute('tabindex', '1');
  document.body.focus();
  document.body.removeAttribute('tabindex');
  document.getElementById('app').scrollTop = 0;
});

// Methods 
const validateOtp = async (val) => {
  errCellPhone.value = '';
  errOfficeEmail.value = '';
  
  // Change from 'Phone' to accept both 'Phone' and 'CellPhone'
  if (val === 'Phone' || val === 'CellPhone') {
    if (otpCellPhone.value) {
      phoneCount.value++;
      const data = {
        mfaType: 1,
        verificationCode: otpCellPhone.value,
        attemptValue: phoneCount.value,
      };
      await store.dispatch(COMMONS.GUEST_VERIFY_MFA_REQUEST, data);
    } else {
      errCellPhone.value = 'Login code is required';
    }
  } 
  // Change from 'Email' to accept both 'Email' and 'OfficeEmail'
  else if (val === 'Email' || val === 'OfficeEmail') {
    if (otpOfficeEmail.value) {
      emailCount.value++;
      const data = {
        mfaType: 2,
        verificationCode: otpOfficeEmail.value,
        attemptValue: emailCount.value,
      };
      await store.dispatch(COMMONS.GUEST_VERIFY_MFA_REQUEST, data);
    } else {
      errOfficeEmail.value = 'Login code is required';
    }
  }
};

const resendOtp = async (val) => {
  errCellPhone.value = '';
  errOfficeEmail.value = '';
  emailCount.value = 0;
  phoneCount.value = 0;
  let data = {};
  
  // Accept both 'Email' and 'OfficeEmail'
  if (val === 'Email' || val === 'OfficeEmail') {
    otpOfficeEmail.value = '';
    data.resendOtp = true;
    data.email = updateContactInfo.value?.email || updateContactInfo.value?.officeEmail;
    data.phone = '';
  }
    
  // Accept both 'Phone' and 'CellPhone'
  if (val === 'Phone' || val === 'CellPhone') {
    otpCellPhone.value = '';
    data.resendOtp = true;
    data.email = '';
    data.phone = updateContactInfo.value?.phone || formatPhoneForRims(updateContactInfo.value?.cellPhone);
  }
  
  await store.dispatch(COMMONS.GUEST_SEND_MFA_REQUEST, data);
};

const handleRouting = async (info) => {
  if (info === 'previous') {
    store.state.guestLogin.contactUpdateStep = 2;
    store.state.networkResponse = null;
  } 
  else if (info === 'next') {
    // Check the store for a pending redirect
    const redirectTo = store.state.guestLogin.navigationSource;

    if (redirectTo) {
      try {
        await store.dispatch(COMMONS.BUSINESS_PROFILE_GET_PROFILE); 
      } catch (error) {
        console.error("Failed to refresh user data", error);
      }
      // Reset the store value so it doesn't redirect again next time
      store.state.guestLogin.navigationSource = null;
      
      router.push({ name: redirectTo });
    } else {
      router.push({ name: 'BusinessProfile' });
    }
  }
};
</script>


<style lang="scss" scoped>
.user-con-validate {
  display: flex;
  gap: 2rem;
  margin: 2rem 0rem;
  .user-con-details {
    display: flex;
    gap: 4rem;
    width: 50%;
    .user-details{
      width: 50%;
      .verify-updateContact{
        display: flex;
        width: 335px;
        label {
          word-wrap: break-word;
          width: 100%;
        }
        .verify-icon{
          width: 50px;
          height: 20px;
          margin-left: 5px;
        }
      }
      input {
        width: 200px;
      }
      .user-phone-details{
        display: flex;
        align-items: baseline;
        width: 335px;
        .user-phone-text{
          margin-left: 5px;
          display: flex;
          font-weight: 300;
          @media only screen and (max-width: 900px) {
            word-wrap: nowrap;
          }
        }
        .user-phone-heading{
          font-weight: bold;
        }
      }
      .err-msg{
        color: var(--ri-red);
      }
      .user-email-heading{
        font-weight: bold;
      }
    }
    @media only screen and (max-width: 699px) {
      display: block;
      .user-details{
        width: 50px;
        .verify-updateContact{
          width: 300px;
        }
        .login-code-text{
          width: 100px;
        }
      }
    }

    @media only screen and (min-width: 700px) and (max-width: 1250px) {
      .user-details{
        .verify-updateContact {
          label {
            width: 200px;
          }
        }
        .user-phone-details {
          width: 200px;
        }
      }
    }
    @media only screen and (min-width: 900px) and (max-width: 1215px) {
      .user-details{
        input {
          width: 145px;
        }
      }
    }
  }
  .user-response {
    margin-top: 2rem;
    display: flex;
    .btn-otp{
      button {
        margin-right: 2rem;
      }
      .resend-code{
        background-color: white;
        color: var(--ri-blue);

        &:hover{
          background-color: var(--ri-lighter-blue)
        }
      }
    }
    @media only screen and (max-width: 899px) {
      .btn-otp{
        .app-btn-primary {
          margin-bottom: 1rem;
        }
      }
    }
    @media only screen and (min-width: 900px) and (max-width: 1160px) {
      display: flex;
      .btn-otp{
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        button {
          margin-right: 2rem;
          min-width: 207px;
        }
      }
    }
  }
  @media only screen and (max-width: 899px) {
    display: block;
  }
}

.non-mfa-info {
  background-color: #f0f8ff;
  border: 1px solid #d0e8ff;
  border-radius: 4px;
  padding: 1rem;
  margin: 2rem 0;
  
  .info-message {
    margin-bottom: 0.5rem;
    color: #333;
  }
  
  ul {
    margin-left: 1.5rem;
    margin-top: 0.5rem;
    
    li {
      color: #555;
      margin-bottom: 0.25rem;
    }
  }
}

.err-email{
  color: var(--ri-red);
}
.err-phone{
  color: var(--ri-red);
}
.app-button-group {
  margin-top: 1rem;
  .app-btn-primary {
    margin-bottom: 1.125em;
    &:not(:last-child) {
      margin-right: 1em;
    }
    margin-left: 1rem;
    &[disabled] {
    cursor: not-allowed;
    opacity: 0.7;
    }
  }
}
</style>